import { Link } from "react-router-dom";
import { Activity, BrainCircuit, Network, ShieldCheck, Radio, Eye, ArrowRight, Terminal } from "lucide-react";

const LOG_LINES = [
  '{"type":"STATUS","source":"verbe","payload":{"health":"ok"}}',
  '{"type":"METRICS","source":"verbe","metrics":{"articles_24h":1}}',
  '{"type":"EVENT","kind":"recommendation","author":"Oracle"}',
  '{"type":"STATUS","source":"eclipse_prod","payload":{"health":"ok"}}',
  '{"type":"ALERT","source":"publia","priority":"high"}',
  '{"ping":"project_health_monitor","interval":"60s"}',
  '{"type":"STATUS","source":"synchronicity","status":"online"}',
  '{"brain":"digestion","notes":"+1","knowledge":"+2"}',
];

const CAPABILITIES = [
  { icon: Eye, label: "[MODULE: OBSERVATION]", title: "Observation temps réel", text: "Chaque projet connecté envoie ses signes de vie. Le Health Monitor vérifie leur état toutes les 60 secondes : ONLINE, DEGRADED ou OFFLINE — rien n'échappe au poste de contrôle.", span: "md:col-span-7" },
  { icon: BrainCircuit, label: "[MODULE: CERVEAU]", title: "Cerveau central IA", text: "Il observe, comprend, mémorise et conseille — avec traçabilité complète des sources réelles utilisées dans chaque raisonnement. Il n'agit jamais seul.", span: "md:col-span-5" },
  { icon: Network, label: "[MODULE: PROTOCOLE]", title: "Protocole universel", text: "Un seul langage JSON pour tous les projets : STATUS, ALERT, METRICS, EVENT. Aucun code spécifique — n'importe quelle IA peut se connecter.", span: "md:col-span-5" },
  { icon: ShieldCheck, label: "[MODULE: SÉCURITÉ]", title: "Sécurité & lecture seule", text: "Clés API individuelles, permissions par projet, provenance étiquetée (production, simulateur, test). Un projet en lecture seule ne reçoit jamais d'ordre.", span: "md:col-span-7" },
];

const STEPS = [
  { n: "01", icon: Radio, title: "Les projets émettent", text: "Heartbeats, métriques, alertes et recommandations affluent vers NEXUS en continu." },
  { n: "02", icon: Activity, title: "NEXUS observe et croise", text: "Chaque donnée est vérifiée, étiquetée par provenance, et confrontée aux autres projets." },
  { n: "03", icon: BrainCircuit, title: "Le Cerveau conseille", text: "Briefings, mémoire, connaissances révisables — des conseils fondés sur le réel, sources à l'appui." },
  { n: "04", icon: ShieldCheck, title: "L'humain décide", text: "NEXUS ne commande jamais seul. Chaque action reste une décision humaine." },
];

const windowCls = "border border-white/10 bg-[#0a0a0a] transition-[transform,border-color] duration-200 hover:-translate-y-1 hover:border-[#00E5FF]/50";

export default function NexusLandingPage() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#050505] text-gray-200">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050505]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Activity className="text-[#00E5FF]" size={20} />
            <span className="font-mono text-sm font-bold tracking-[0.35em] text-white">NEXUS</span>
          </div>
          <Link to="/login" data-testid="landing-login-cta"
            className="flex items-center gap-2 border border-[#00E5FF]/50 px-5 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-[#00E5FF] transition-colors duration-200 hover:bg-[#00E5FF] hover:text-black">
            Connexion <ArrowRight size={13} />
          </Link>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-white/10">
        <div className="pointer-events-none absolute inset-0 opacity-15"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1750969185331-e03829f72c7d?crop=entropy&cs=srgb&fm=jpg&q=85)", backgroundSize: "cover", backgroundPosition: "center", maskImage: "linear-gradient(to bottom, black, transparent)" }} />
        <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 py-24 md:grid-cols-12 md:py-32">
          <div className="md:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#00E5FF]">
              Système nerveux central<span className="animate-pulse">_</span>
            </p>
            <h1 className="mt-5 text-5xl font-bold tracking-tight text-white sm:text-6xl lg:text-7xl">
              Un écosystème d'IA.<br />
              <span className="text-[#00E5FF] drop-shadow-[0_0_12px_rgba(0,229,255,0.4)]">Un seul poste de contrôle.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-gray-400 sm:text-lg">
              NEXUS connecte des projets d'intelligence artificielle autonomes à un Cerveau central
              qui observe, comprend, mémorise — et conseille. Sans jamais agir seul.
            </p>
            <div className="mt-10 flex items-center gap-5">
              <Link to="/login" data-testid="hero-login-cta"
                className="flex items-center gap-2 bg-[#00E5FF] px-7 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-black transition-opacity duration-200 hover:opacity-85">
                Connexion <ArrowRight size={14} />
              </Link>
              <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-[#00E676]">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#00E676]" /> Écosystème sous surveillance
              </span>
            </div>
          </div>
          <div className="hidden md:col-span-4 md:block">
            <div className="h-full border border-white/10 bg-[#0a0a0a]">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2">
                <Terminal size={12} className="text-[#00E676]" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">flux — temps réel</span>
              </div>
              <div className="space-y-2 overflow-hidden px-4 py-4">
                {LOG_LINES.map((l, i) => (
                  <p key={l} className="truncate font-mono text-[10px] leading-relaxed text-[#00E676]/80"
                    style={{ opacity: 1 - i * 0.09 }}>
                    <span className="text-neutral-600">{String(i + 1).padStart(2, "0")} </span>{l}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-24">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#00E5FF]">Capacités</p>
        <h2 className="mt-3 text-base font-bold text-white md:text-lg">Quatre modules, une mission : la clarté.</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-12">
          {CAPABILITIES.map((c) => (
            <div key={c.title} className={`${windowCls} ${c.span}`}>
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-2.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">{c.label}</span>
                <c.icon size={14} className="text-[#00E5FF]" />
              </div>
              <div className="px-5 py-6">
                <h3 className="text-base font-bold text-white">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-400">{c.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#070707]">
        <div className="mx-auto max-w-6xl px-6 py-24">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#00E5FF]">Fonctionnement</p>
          <h2 className="mt-3 text-base font-bold text-white md:text-lg">Du signal à la décision.</h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-4">
            {STEPS.map((s) => (
              <div key={s.n} className="border border-dashed border-white/15 px-5 py-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-2xl font-bold text-[#00E5FF]/40">{s.n}</span>
                  <s.icon size={16} className="text-[#00E676]" />
                </div>
                <h3 className="mt-4 text-sm font-bold text-white">{s.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-gray-500">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="mx-auto max-w-6xl px-6 py-20 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-neutral-500">Accès réservé à l'opérateur</p>
          <h2 className="mx-auto mt-4 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Le poste de contrôle vous attend.
          </h2>
          <Link to="/login" data-testid="footer-login-cta"
            className="mt-8 inline-flex items-center gap-2 border border-[#00E5FF]/50 px-8 py-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#00E5FF] transition-colors duration-200 hover:bg-[#00E5FF] hover:text-black">
            Connexion <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 md:flex-row">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-600">NEXUS — brainnexora.com</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-700">Observer · Comprendre · Conseiller — jamais agir seul</span>
        </div>
      </footer>
    </div>
  );
}
